import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { ApplicationSettingsModel } from '@fi-app/role/application-settings.model';
import { IApplicationSettingsTypeGroupLabels } from '@fi-app/role/role';
import { AccessRightType, ApplicationSettingsType, ApplicationSettingsTypeGroup } from '@fi-shared/types';
import { RoleHelperService } from '../../../../../services/role-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { isDefined } from '@uirouter/core';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Subject } from 'rxjs';

@Component({
    selector: 'fi-role-application-setting-edit-dialog',
    templateUrl: './role-application-setting-edit-dialog.component.html',
    styleUrls: ['./role-application-setting-edit-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-role-application-setting-edit-dialog' },
    standalone: false,
})
export class RoleApplicationSettingEditDialogComponent
    extends FiDialogsContentBase<ApplicationSettingsModel[], ApplicationSettingsModel[]>
    implements OnInit, OnDestroy
{
    private roleHelperService: RoleHelperService;
    private appTextService: AppTextService;
    private cdr: ChangeDetectorRef;
    private fiDialogsService: FiDialogsService;

    applicationSettings: ApplicationSettingsModel[] = [];
    groupLabels: IApplicationSettingsTypeGroupLabels;
    AccessRightType = AccessRightType;
    fiValuesComparison: FiValuesComparison<ApplicationSettingsModel[]>;

    ngOnInit(): void {
        this.copyData();
        this.setServices();
        this.setGroupLabels(this.applicationSettings[0]);
        this.fiValuesComparison = new FiValuesComparison(() => this.applicationSettings);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    private copyData() {
        this.applicationSettings = this.data.map((applicationSetting: ApplicationSettingsModel) => {
            return new ApplicationSettingsModel(
                applicationSetting.RoleId,
                applicationSetting.ApplicationSettingType,
                applicationSetting.AccessRightType,
                applicationSetting.AccessRightTypes,
            );
        });
    }

    onAccessRightTypeSliderValueChange($event: AccessRightType, applicationSetting: ApplicationSettingsModel) {
        applicationSetting.AccessRightType = $event;
        this.setApplicationSettingsRelatedSettings(applicationSetting);
    }

    private setApplicationSettingsRelatedSettings(applicationSetting: ApplicationSettingsModel) {
        // https://chainformation.atlassian.net/browse/FI-3481 special case
        const isSpecialCase =
            this.isFormRelatedApplicationSettings(applicationSetting.ApplicationSettingType) ||
            this.isForumRelatedApplicationSettings(applicationSetting.ApplicationSettingType) ||
            this.isChecklistRelatedApplicationSettings(applicationSetting.ApplicationSettingType);

        this.updateDefaultSettings(applicationSetting, {
            skipSetIsCheckedToAllRelatives: isSpecialCase,
        });

        if (this.isFormRelatedApplicationSettings(applicationSetting.ApplicationSettingType)) {
            this.updateFormSettings(applicationSetting);
        } else if (this.isForumRelatedApplicationSettings(applicationSetting.ApplicationSettingType)) {
            this.updateForumSettings(applicationSetting);
        } else if (this.isChecklistRelatedApplicationSettings(applicationSetting.ApplicationSettingType)) {
            this.updateChecklistSettings(applicationSetting);
        }
    }

    private updateDefaultSettings(
        applicationSetting: ApplicationSettingsModel,
        args: {
            skipSetIsCheckedToAllRelatives: boolean;
        },
    ) {
        const isIndependentModuleRightsGroups = this.roleHelperService.isIndependentModuleRightsGroups(
            applicationSetting.ApplicationSettingType,
        );
        if (isIndependentModuleRightsGroups) {
            args.skipSetIsCheckedToAllRelatives = true;
        }

        let connectedApplicationSettings = this.roleHelperService.connectedApplicationSettings.find(
            (items) => items.indexOf(applicationSetting.ApplicationSettingType) !== -1,
        );

        if (isDefined(connectedApplicationSettings)) {
            const currentIdx = connectedApplicationSettings.indexOf(applicationSetting.ApplicationSettingType);

            const isApplicationSettingAvailable =
                this.roleHelperService.isApplicationSettingsModelAvailable(applicationSetting);
            connectedApplicationSettings.forEach((appSetting, idx) => {
                if (idx < currentIdx) {
                    // check all lower settings in group (update local when change central)

                    const relatedLocalSetting = this.applicationSettings.find((setting: ApplicationSettingsModel) => {
                        return setting.ApplicationSettingType === appSetting;
                    });

                    let newAccessRightType = relatedLocalSetting.AccessRightType;
                    if (!isApplicationSettingAvailable && !isIndependentModuleRightsGroups) {
                        // don't disable local core rights if central not available
                        newAccessRightType = relatedLocalSetting.AccessRightTypes[0];
                    }
                    if (relatedLocalSetting.AccessRightType < applicationSetting.AccessRightType) {
                        if (relatedLocalSetting.AccessRightTypes.indexOf(applicationSetting.AccessRightType) !== -1) {
                            newAccessRightType = applicationSetting.AccessRightType;
                        } else {
                            newAccessRightType =
                                relatedLocalSetting.AccessRightTypes[relatedLocalSetting.AccessRightTypes.length - 1];
                        }
                    }
                    if (newAccessRightType !== relatedLocalSetting.AccessRightType) {
                        relatedLocalSetting.AccessRightType = newAccessRightType;
                    }
                } else if (idx > currentIdx) {
                    // check all higher settings in group (update central when change local)

                    const relatedCentralSetting = this.applicationSettings.find((setting: ApplicationSettingsModel) => {
                        return setting.ApplicationSettingType === appSetting;
                    });

                    let newAccessRightType = relatedCentralSetting.AccessRightType;
                    if (!isApplicationSettingAvailable) {
                        newAccessRightType = AccessRightType.UnAvailable;
                    }
                    if (relatedCentralSetting.AccessRightType > applicationSetting.AccessRightType) {
                        newAccessRightType = applicationSetting.AccessRightType;
                    }
                    if (newAccessRightType !== relatedCentralSetting.AccessRightType) {
                        relatedCentralSetting.AccessRightType = newAccessRightType;
                    }
                }
            });
        }

        if (!args.skipSetIsCheckedToAllRelatives) {
            this.setIsChecked();
        }
    }

    private updateFormSettings(applicationSetting: ApplicationSettingsModel) {
        const formReportsApplicationSetting: ApplicationSettingsModel = this.getApplicationSettingsByType(
            ApplicationSettingsType.FormReports,
        );

        const formLocalApplicationSetting: ApplicationSettingsModel = this.getApplicationSettingsByType(
            ApplicationSettingsType.FormLocal,
        );

        const formCentralApplicationSetting: ApplicationSettingsModel = this.getApplicationSettingsByType(
            ApplicationSettingsType.FormCentral,
        );

        const isApplicationSettingsAvailable = !this.isAccessRightEqualsUnavailable(applicationSetting.AccessRightType);

        if (applicationSetting.ApplicationSettingType === ApplicationSettingsType.FormLocal) {
            if (isApplicationSettingsAvailable) {
                if (
                    applicationSetting.AccessRightType === AccessRightType.Share &&
                    this.isAccessRightEqualsUnavailable(formReportsApplicationSetting.AccessRightType)
                ) {
                    formReportsApplicationSetting.AccessRightType = AccessRightType.Write;
                }
            } else {
                formReportsApplicationSetting.AccessRightType = AccessRightType.UnAvailable;
            }
        }

        if (applicationSetting.ApplicationSettingType === ApplicationSettingsType.FormCentral) {
            if (isApplicationSettingsAvailable) {
                if (
                    formLocalApplicationSetting.AccessRightType === AccessRightType.Share &&
                    this.isAccessRightEqualsUnavailable(formReportsApplicationSetting.AccessRightType)
                ) {
                    formReportsApplicationSetting.AccessRightType = AccessRightType.Write;
                }
            }
        }

        if (applicationSetting.ApplicationSettingType === ApplicationSettingsType.FormReports) {
            if (isApplicationSettingsAvailable) {
                if (this.isAccessRightEqualsUnavailable(formLocalApplicationSetting.AccessRightType)) {
                    formLocalApplicationSetting.AccessRightType = AccessRightType.Read;
                }
                if (this.isAccessRightEqualsUnavailable(formCentralApplicationSetting.AccessRightType)) {
                    formCentralApplicationSetting.AccessRightType = AccessRightType.Read;
                }
            } else {
                if (formLocalApplicationSetting.AccessRightType > AccessRightType.Read) {
                    formLocalApplicationSetting.AccessRightType = AccessRightType.Read;
                }
                if (formCentralApplicationSetting.AccessRightType > AccessRightType.Read) {
                    formCentralApplicationSetting.AccessRightType = AccessRightType.Read;
                }
            }
        }

        this.cdr.detectChanges();
    }

    private isAccessRightEqualsUnavailable(accessRightType: AccessRightType): boolean {
        return accessRightType === AccessRightType.UnAvailable;
    }

    private updateForumSettings(applicationSettingType: ApplicationSettingsModel) {
        const forumApplicationSetting: ApplicationSettingsModel = this.getApplicationSettingsByType(
            ApplicationSettingsType.Forum,
        );

        const forumAdminApplicationSetting: ApplicationSettingsModel = this.getApplicationSettingsByType(
            ApplicationSettingsType.ForumAdmin,
        );

        const isApplicationSettingsAvailable: boolean = !this.isAccessRightEqualsUnavailable(
            applicationSettingType.AccessRightType,
        );

        if (
            applicationSettingType.ApplicationSettingType === ApplicationSettingsType.Forum &&
            !isApplicationSettingsAvailable
        ) {
            forumAdminApplicationSetting.AccessRightType = AccessRightType.UnAvailable;
        } else if (
            applicationSettingType.ApplicationSettingType === ApplicationSettingsType.ForumAdmin &&
            !this.isAccessRightEqualsUnavailable(applicationSettingType.AccessRightType)
        ) {
            forumApplicationSetting.AccessRightType = AccessRightType.Share;
        }
    }

    private updateChecklistSettings(applicationSetting: ApplicationSettingsModel) {
        const checklistReportsApplicationSetting: ApplicationSettingsModel = this.getApplicationSettingsByType(
            ApplicationSettingsType.CheckListReports,
        );

        /*const checklistLocalApplicationSetting: ApplicationSettingsModel = this.getApplicationSettingsByType(
            ApplicationSettingsType.ChecklistLocal);*/

        if (applicationSetting.ApplicationSettingType === ApplicationSettingsType.CheckListReports) {
            checklistReportsApplicationSetting.AccessRightType = applicationSetting.AccessRightType;
        }

        this.cdr.detectChanges();
    }

    private isFormRelatedApplicationSettings(applicationSettingsType: ApplicationSettingsType): boolean {
        return (
            this.roleHelperService.getAccessRightTypeGroup(applicationSettingsType) ===
            ApplicationSettingsTypeGroup.Form
        );
    }

    private isForumRelatedApplicationSettings(applicationSettingsType: ApplicationSettingsType): boolean {
        return (
            this.roleHelperService.getAccessRightTypeGroup(applicationSettingsType) ===
            ApplicationSettingsTypeGroup.Forum
        );
    }

    private isChecklistRelatedApplicationSettings(applicationSettingsType: ApplicationSettingsType): boolean {
        return (
            this.roleHelperService.getAccessRightTypeGroup(applicationSettingsType) ===
            ApplicationSettingsTypeGroup.Checklist
        );
    }

    private setIsChecked() {
        const isAllEnabled: boolean = this.applicationSettings.every((applicationSetting: ApplicationSettingsModel) => {
            return applicationSetting.AccessRightType !== AccessRightType.UnAvailable;
        });

        this.applicationSettings.forEach((applicationSetting: ApplicationSettingsModel) => {
            if (!isAllEnabled) {
                applicationSetting.AccessRightType = AccessRightType.UnAvailable;
            }
        });
    }

    getApplicationSettingName(applicationSetting: ApplicationSettingsModel): string {
        return this.roleHelperService.getApplicationSettingName(applicationSetting.ApplicationSettingType);
    }

    getAccessLevelName(applicationSetting: ApplicationSettingsModel, accessRightType: AccessRightType): string {
        return this.appTextService.get(
            this.roleHelperService.getAccessLevelName(applicationSetting.ApplicationSettingType, accessRightType),
        );
    }

    getApplicationSettingColor(accessRightType: AccessRightType): string {
        return this.roleHelperService.getApplicationSettingColor(accessRightType);
    }

    private setServices() {
        this.roleHelperService = this.injector.get(RoleHelperService);
        this.appTextService = this.injector.get(AppTextService);
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.fiDialogsService = this.injector.get(FiDialogsService);
    }

    private setGroupLabels(applicationSetting: ApplicationSettingsModel) {
        const applicationSettingTypeGroup: ApplicationSettingsTypeGroup =
            this.roleHelperService.getAccessRightTypeGroup(applicationSetting.ApplicationSettingType);
        this.groupLabels = this.roleHelperService.getApplicationSettingsTypeGroupLabels(applicationSettingTypeGroup);
    }

    private getApplicationSettingsByType(applicationSettingsType: ApplicationSettingsType): ApplicationSettingsModel {
        return this.applicationSettings.find((settings: ApplicationSettingsModel) => {
            return settings.ApplicationSettingType === applicationSettingsType;
        });
    }

    getResult(): ApplicationSettingsModel[] {
        return this.applicationSettings;
    }

    getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
